import { useAccount } from '@/domain/hooks/useAccount'
import * as hl from '@nktkas/hyperliquid'
import { useQuery } from '@tanstack/react-query'

const transport = new hl.HttpTransport()
const publicClient = new hl.InfoClient({ transport })

export interface UseIsHypercoreUserResult {
  /** Whether the user has an activated Hypercore account */
  isHypercoreUser: boolean
  /** Whether the query is still loading */
  isLoading: boolean
  /** Any error that occurred */
  error: Error | null
}

/**
 * Hook to check if the current user has an activated Hypercore account.
 *
 * A user is considered a Hypercore user if they have ever received a Core asset
 * (USDC, HYPE, etc.) which activates their account. Once activated, the account
 * persists even if the user later withdraws all funds.
 *
 * This is used to determine eligibility for features like big blocks toggle.
 */
export function useIsHypercoreUser(): UseIsHypercoreUserResult {
  const account = useAccount()

  const { data, isLoading, error } = useQuery({
    queryKey: ['hypercore-user-check', account],
    queryFn: async () => {
      if (!account) {
        return false
      }

      try {
        const result = await publicClient.preTransferCheck({
          user: account,
          source: account,
        })
        return result.userExists
      } catch (err) {
        console.error('Failed to check Hypercore user status:', err)
        // If the check fails, assume not a Hypercore user (safer default)
        return false
      }
    },
    enabled: !!account,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes - account status rarely changes
    gcTime: 10 * 60 * 1000,
  })

  return {
    isHypercoreUser: data ?? false,
    isLoading,
    error: error as Error | null,
  }
}
